import { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, Form, useFormikContext } from 'formik';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { Message } from 'semantic-ui-react';
import 'semantic-ui-css/semantic.min.css';
import '../../components/jsonEditor/jsonEditor.css';

import {
  addStoryBookItem,
  fetchFullBook,
  moveAllFiles,
  uploadHtmlToS3,
  uploadFileToS3,
  uploadPdfFileToS3,
  processUploadedPdf,
  fetchUserAndBookStatistics,
  clearStory
} from '../../slices/storyEditorSlice';
import { AuthStdFooter } from "../../utils/auth-std-footer";

import PdfViewer from '../../components/aaiPdfViewer';
import StoryImporterToolbar from '../../components/StoryImporterToolbar';
import UtilityModal from '../../utils/modal-helper';
import { fetchSignedUrlByType } from '../../utils/fetchSignedUrlByFolderV2';

import storyInjestion from '../../assets/story-editor-injestion.jpg';

/**
 * StoryImporter Component
 * Streamlined version of StoryEditor for use in panels
 * - No header section
 * - No processing hint
 * - Pre-selected story required (no dropdown)
 */
const StoryImporter = ({ titleText, preSelectedBook, preSelectedLitGroup }) => {
  const dispatch = useDispatch();
  const userProfile = useSelector(state => state.user.profile);
  const [modalOpen, setModalOpen] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [formData, setFormData] = useState('<p>Initial data</p>');

  const storySelectedObj = useRef({});
  const thumbnailSignedUrl = useRef({});
  const contentSignedUrl = useRef({});
  const formContent = useRef("");
  const activeElapsedTime = useRef(0);

  // Validate that we have a required book selection
  useEffect(() => {
    if (!preSelectedBook || !preSelectedBook.uuid) {
      setValidationError('Invalid book selection. Please close this panel and select a valid book.');
      return;
    }

    // Set up the selected book object
    const bookOption = {
      key: preSelectedBook.name?.replace(/[^a-z0-9]/gi, '').toLowerCase() || preSelectedBook.uuid,
      text: preSelectedBook.name || 'Unknown Book',
      value: preSelectedBook.uuid,
      data: preSelectedBook
    };

    storySelectedObj.current = bookOption;
    setValidationError(null);

    // Fetch book data
    if (userProfile?.data?.userId) {
      dispatch(fetchUserAndBookStatistics(preSelectedBook.uuid)).unwrap().catch(console.error);
      getFullBook(preSelectedBook.uuid);
    }
  }, [preSelectedBook, userProfile, dispatch]);

  const getFullBook = (bookUuid) => {
    if (!userProfile?.data?.userId) {
      console.log('User profile not loaded yet, skipping getFullBook');
      return;
    }

    dispatch(fetchFullBook({ userId: userProfile.data.userId, bookUuid: bookUuid }))
      .unwrap()
      .then(data => {
        console.log('fetchFullBook success', data);
      })
      .catch(error => {
        console.error('fetchFullBook error:', error);
      });
  };

  const initialValues = {
    title: '',
    author: '',
    startDate: new Date(),
    updatedDate: new Date(),
    category: ["1"],
    storyFileName: null,
    thumbnail: null,
    excerpt: '',
    content: '',
    contentUrl: "./shadowResponse.pdf",
    enabled: true,
    selectedStory: preSelectedBook?.uuid || '',
    activeStoryObj: preSelectedBook ? {
      key: preSelectedBook.name?.replace(/[^a-z0-9]/gi, '').toLowerCase() || preSelectedBook.uuid,
      text: preSelectedBook.name || 'Unknown Book',
      value: preSelectedBook.uuid,
      data: preSelectedBook
    } : {},
    pdfFile: null
  };

  useEffect(() => {
    dispatch(clearStory());
  }, [dispatch]);

  const UpdateFieldValue = ({ formData }) => {
    const { setFieldValue } = useFormikContext();

    useEffect(() => {
      setFieldValue('content', formData);
    }, [setFieldValue, formData]);

    return null;
  };

  const handleModalClose = useCallback(() => setModalOpen(false), []);
  const handleModalCancel = useCallback(() => setModalOpen(false), []);

  const handleModalSubmit = () => {
    setModalOpen(false);
  };

  const handleClear = (resetForm) => {
    setModalOpen(false);
    resetForm({ values: initialValues });
  };

  const submitForm = async (formValues, processStatistics) => {
    if (!userProfile?.data?.userId) {
      console.log('User profile not loaded, cannot submit form');
      return;
    }

    const saveBookParams = {
      userId: userProfile.data.userId,
      bookUuid: storySelectedObj.current.data.uuid || uuidv4(),
      fileName: formValues.pdfFile.name.split('.').slice(0, -1).join('.'),
      thumbnail: formValues.thumbnail.name,
      ...processStatistics
    };
    console.log('submitForm', saveBookParams);
    dispatch(addStoryBookItem(saveBookParams));
    dispatch(clearStory());
  };

  const handleFileURLSelection = (url) => {
    console.log('fileURLCallback: url', url);
  };

  const onSubmit = async (values, { resetForm }) => {
    if (!userProfile?.data?.userId) {
      console.log('User profile not loaded, cannot submit');
      return;
    }

    const { storyFileName, thumbnail, title, activeStoryObj, pdfFile } = values;
    if (_.isEmpty(storyFileName) ||
      _.isEmpty(thumbnail) ||
      _.isEmpty(title) ||
      _.isEmpty(activeStoryObj)) {
      console.log('Missing Submit Required fields', values);
      return;
    }

    const bookTypeName = 'book-process-items';
    thumbnailSignedUrl.current.signedUrl = await fetchSignedUrlByType({
      requestType: 'BOOK_ELEMENTS_IMAGE',
      userId: userProfile.data.userId,
      bookUuid: storySelectedObj.current.data.uuid,
      fileName: thumbnail.name
    });

    const selectedPdfFileName = await uploadPdfFileToS3(pdfFile, userProfile.data.userId, activeStoryObj.data.uuid);

    console.log('upload source to S3 ', selectedPdfFileName);
    const uploadData = await processUploadedPdf(selectedPdfFileName, userProfile.data.userId, activeStoryObj.data.uuid);

    console.log('uploadBookSource data', uploadData);
    const processStatistics = {
      processTime: uploadData.processingTime,
      UserStatistics: uploadData.UserStatistics,
      BookStatistics: uploadData.BookStatistics,
      ...uploadData.TokenAccumulator
    };

    console.log('Statistics', processStatistics);

    await uploadFileToS3(thumbnailSignedUrl.current.signedUrl, thumbnail);
    await moveAllFiles(userProfile.data.userId, activeStoryObj.data.uuid, formContent, values.content);

    let storyName = title;
    if (storyName === "") {
      storyName = storyFileName.name.split('.');
      storyName = storyName[0];
    }
    const cleanedTitle = storyName.replace(/[^a-z0-9]/gi, '').toLowerCase();

    contentSignedUrl.current.signedUrl = await fetchSignedUrlByType({
      requestType: 'ADMIN_PATH',
      userId: userProfile.data.userId,
      rootGroup: 'books',
      infoType: bookTypeName,
      folderName: 'content',
      fileName: `${cleanedTitle}.html`
    });

    await uploadHtmlToS3(contentSignedUrl.current.signedUrl, formContent.current);
    await submitForm(values, processStatistics);

    resetForm({ values: initialValues });
    activeElapsedTime.current = 0;

    console.log('About to refresh after submit');
    dispatch(fetchUserAndBookStatistics(storySelectedObj.current.data.uuid)).unwrap();
    getFullBook(storySelectedObj.current.data.uuid);
  };

  // Show error message if no valid book selected
  if (validationError) {
    return (
      <div style={{ marginBottom: "70px", padding: "1rem" }}>
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{validationError}</p>
        </Message>
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize={false}
    >
      {({ values, setFieldValue, resetForm, isSubmitting, isValid, dirty }) => (
        <div style={{ maxWidth: "100%", padding: "1rem" }}>
          <Form>
            <UpdateFieldValue formData={formData} />

            {/* Display selected book name as read-only */}
            {preSelectedBook && 1 !== 1 && (
              <Message info size="tiny" style={{ marginBottom: "1rem" }}>
                {/* <Message.Header>Selected Book</Message.Header> */}
                {/* <p>{preSelectedBook.name || 'Unknown Book'}</p>
                <p>{values.author || 'Unknown Author'}</p>

                <p>{preSelectedBook.uuid || 'Unknown Uuid'}</p> */}
                <dl className="custom-toolbar-filename-helper">
                  <dt>Book:</dt>
                  <dd>{preSelectedBook?.name || <span>*Book required...</span>}</dd>

                  <dt>Author:</dt>
                  <dd>{values.author || <span>*Unknown Book...</span>}</dd>
                  <dt>Book Id:</dt>
                  <dd style={{ overflow: "auto", width: "auto" }}>{preSelectedBook.uuid || <span>*Author required...</span>}</dd>
                </dl>


              </Message>
            )}

            {/* Simplified UI with dedicated StoryImporter toolbar */}
            <Message className="importerMessageBox">
              <StoryImporterToolbar
                toolBarCallback={handleFileURLSelection}
                disabled={false}
                values={values}
                isSubmitting={isSubmitting}
                storySelectedObj={storySelectedObj}
                getFullBook={getFullBook}
                preSelectedBook={preSelectedBook}
              />
            </Message>

            {/* PDF Viewer */}
            <div style={{ marginTop: "1rem", width: "100%", zIndex: "4000", marginBottom: "50px" }}>
              <PdfViewer />
            </div>
          </Form>

          <UtilityModal
            isOpen={modalOpen}
            handleModalClose={handleModalClose}
            handleCancel={handleModalCancel}
            handleOverwrite={() => handleClear(resetForm)}
            handleSave={handleModalSubmit}
            promptObj={{
              image: storyInjestion,
              modal_header: 'Warning - Unsaved Data',
              header_text: 'Complete Story Injection, Editing, Overwrite or Save',
              description_text: <div>
                <p>You have unsaved data. This data will be lost if you choose Overwrite. If you Cancel, you can continue editing your existing changes. Or, you can save your changes and proceed with your new selection.</p>
                <p>Cancel, Clear, or Save Data?</p></div>,
              button_neg_text: 'Cancel',
              button_pos_text: "Clear Form",
              button_save_text: "Submit"
            }}
          />
        </div>
      )}
    </Formik>
  );
};

export default StoryImporter;
